/**
 * addStoreNote — POST /api/retailers/stores/{storeId}/notes/.
 * No parent dependency. applyLocal appends the note to a per-store
 * singleton cache so the Store Notes UI updates instantly.
 */

import type { WriteOp, SyncResult } from '../types';
import { STORES, get, put } from '../../utils/indexedDB';
import { BASE_URL, jsonHeaders, classifyResponse, networkErrorResult } from '../httpHelpers';

export interface AddStoreNoteInput {
  storeId: number;
  content: string;
  authorName?: string;
}

export interface AddStoreNoteResponse {
  id: number;
}

interface StoreNotesSingleton {
  key: string;
  notes: Array<{
    localId: string;
    content: string;
    authorName?: string;
    createdAt: string;
    serverId?: number;
  }>;
}

function notesKey(storeId: number): string {
  return `storeNotes:${storeId}`;
}

export const addStoreNoteOp: WriteOp<AddStoreNoteInput, AddStoreNoteInput, AddStoreNoteResponse> = {
  kind: 'addStoreNote',

  async applyLocal(input, localId) {
    const key = notesKey(input.storeId);
    const existing =
      (await get<StoreNotesSingleton>(STORES.VISIT_SINGLETONS, key)) ||
      ({ key, notes: [] } as StoreNotesSingleton);
    existing.notes.push({
      localId,
      content: input.content,
      authorName: input.authorName,
      createdAt: new Date().toISOString(),
    });
    await put(STORES.VISIT_SINGLETONS, existing);
  },

  serialize(input, localId) {
    return {
      action: 'addStoreNote',
      endpoint: `/api/retailers/stores/${input.storeId}/notes/`,
      method: 'POST',
      payload: input,
      priority: 70,
      maxRetries: 10,
      dependsOn: null,
      relatedTaskUUID: localId,
      idempotencyKey: localId,
    };
  },

  async sync(row): Promise<SyncResult> {
    const input = row.payload as AddStoreNoteInput;

    let response: Response;
    try {
      response = await fetch(`${BASE_URL}${row.endpoint}`, {
        method: row.method,
        headers: jsonHeaders(row.idempotencyKey),
        body: JSON.stringify({ content: input.content }),
      });
    } catch (err) {
      return networkErrorResult(err);
    }

    const classified = await classifyResponse(response);
    if (classified) return classified;

    const data = await response.json().catch(() => ({}));
    return {
      status: 'success',
      serverId: typeof data?.id === 'number' ? data.id : undefined,
      retriable: false,
    };
  },

  async reconcile(row, response) {
    const input = row.payload as AddStoreNoteInput;
    const localId = row.relatedTaskUUID;
    if (!localId || typeof response.id !== 'number') return;
    const key = notesKey(input.storeId);
    const existing = await get<StoreNotesSingleton>(STORES.VISIT_SINGLETONS, key);
    if (!existing) return;
    const match = existing.notes.find((n) => n.localId === localId);
    if (match) match.serverId = response.id;
    await put(STORES.VISIT_SINGLETONS, existing);
  },

  dependsOn() {
    return null;
  },
};

export type AddStoreNoteOp = typeof addStoreNoteOp;
